import { useCallback, useState } from 'react';
import { Form, FormProps, notification } from 'antd';
import { useAdminPasswordResetMutation } from '../../../../api';
import { ErrorMappingValueType } from '../../../../typings';
import { AdminForgotPasswordFieldType } from '../../../../typings/auth';

interface WarehouseForgotPasswordFieldType {
  email: string | null;
}

export const useWarehouseForgotPassword = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [form] = Form.useForm();
  const [resetPassword, { isLoading }] = useAdminPasswordResetMutation();
  const [notificationApi, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const handleErrors = useCallback(
    (error: any) => {
      if (!error?.data?.success && error?.data?.data) {
        Object.values(error.data.data).forEach((message) => {
          openNotificationWithIcon({
            type: 'error',
            message: message as string,
          });
        });
      } else {
        openNotificationWithIcon({
          type: 'error',
          message: 'Something went wrong!',
        });
      }
    },
    [openNotificationWithIcon]
  );

  const onFinish: FormProps<WarehouseForgotPasswordFieldType>['onFinish'] = useCallback(
    async (values: WarehouseForgotPasswordFieldType) => {
      try {
        // Add isWarehouseUser parameter to the payload
        const payload: AdminForgotPasswordFieldType & { isWarehouseUser?: boolean } = {
          ...values,
          isWarehouseUser: true,
        };

        const response = await resetPassword(payload);
        if (response?.data?.success) {
          setIsModalOpen(true);
        } else {
          handleErrors(response.error);
        }
      } catch (err) {
        handleErrors(err);
      }
    },
    [resetPassword, handleErrors]
  );

  const onModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onModalOk = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return {
    contextHolder,
    isModalOpen,
    onModalClose,
    onModalOk,
    form,
    onFinish,
    isLoading,
  };
};
